import { __assign, __spreadArrays } from "tslib";
// tslint:disable-next-line:no-var-requires
var deepEqual = require('deep-equal');
var createResponseWrapper = function (request, response, handlers, success) {
    var calls = 0;
    var resolvePromise;
    var onMatch = function () {
        calls++;
        if (resolvePromise) {
            resolvePromise();
            resolvePromise = undefined;
        }
    };
    handlers.push({ request: request, response: response, success: success, onMatch: onMatch });
    return {
        called: function () { return calls > 0; },
        calls: function () { return calls; },
        whenCalled: function () { return new Promise(function (resolve) { return resolvePromise = resolve; }); }
    };
};
// tslint:disable-next-line:max-line-length
var createRequestWrapper = function (request, handlers, onUploadProgress) {
    return {
        respond: function (response) {
            return createResponseWrapper(request, response, handlers, true);
        },
        throw: function (err) {
            return createResponseWrapper(request, err, handlers, false);
        },
        triggerUploadProgress: function (loaded, total) {
            if (onUploadProgress) {
                // tslint:disable-next-line:no-object-literal-type-assertion
                onUploadProgress({ type: 'dummy', loaded: loaded, total: total });
            }
            else {
                throw new Error('Error triggering progress: on progress callback not set');
            }
        }
    };
};
var deleteUndefined = function (obj) {
    var copyObj = __assign({}, obj);
    Object.keys(copyObj).forEach(function (key) {
        if (typeof copyObj[key] === 'undefined') {
            delete copyObj[key];
        }
    });
    return copyObj;
};
var compareBody = function (body1, body2) {
    return deepEqual(deleteUndefined(body1), deleteUndefined(body2));
};
export var createHttpTestKit = function () {
    var handlers = [];
    var requests = [];
    var testKit = {
        whenGet: function (url) {
            var request = { url: url, method: 'GET', headers: {} };
            return createRequestWrapper(request, handlers);
        },
        whenDelete: function (url) {
            var request = { url: url, method: 'DELETE', headers: {} };
            return createRequestWrapper(request, handlers);
        },
        whenPost: function (url, body, onUploadProgress) {
            var request = { url: url, method: 'POST', body: body };
            return createRequestWrapper(request, handlers, onUploadProgress);
        },
        whenPut: function (url, body) {
            var request = { url: url, method: 'PUT', body: body };
            return createRequestWrapper(request, handlers);
        },
        calls: function () { return requests.length; },
        lastRequest: function () { return requests[0]; },
        lastPostRequest: function () {
            return requests.filter(function (request) { return request.method === 'POST'; })[0];
        },
        lastGetRequest: function () {
            return requests.filter(function (request) { return request.method === 'GET'; })[0];
        },
        lastPutRequest: function () {
            return requests.filter(function (request) { return request.method === 'PUT'; })[0];
        }
    };
    var http = {
        get: function (url, headers) {
            var foundHandler = handlers.filter(function (handler) {
                var isWildcard = handler.request.url === '*';
                return isWildcard || handler.request.url === url && handler.request.method === 'GET';
            })[0];
            requests = __spreadArrays([{ method: 'GET', url: url, headers: headers }], requests);
            if (foundHandler) {
                var success = foundHandler.success, response = foundHandler.response;
                foundHandler.onMatch();
                return success ? Promise.resolve(response) : Promise.reject(response);
            }
            else {
                console.warn('Request hit to unmocked url', url);
                return Promise.reject(new Error("Not mocked: [" + url + "]"));
            }
        },
        put: function (url, body, headers) {
            var lastBodyOnlyMismatch = null;
            var foundHandler = handlers.filter(function (handler) {
                if (handler.request.method === 'PUT') {
                    var handlerBody = handler.request.body;
                    var bodyMatch = !!body && compareBody(body, handlerBody);
                    var isWildcard = handler.request.url === '*';
                    var urlsMatch = (handler.request.url === url);
                    if (urlsMatch && !bodyMatch) {
                        // tslint:disable-next-line:max-line-length
                        lastBodyOnlyMismatch = "Found match by url but body does not match. Expected: " + JSON.stringify(handlerBody) + " but got " + JSON.stringify(body);
                    }
                    return isWildcard || (handler.request.url === url) && (bodyMatch || handlerBody === '*');
                }
                else {
                    return false;
                }
            })[0];
            requests = __spreadArrays([{ method: 'PUT', url: url, body: body, headers: headers }], requests);
            if (foundHandler) {
                foundHandler.onMatch();
                var success = foundHandler.success, response = foundHandler.response;
                return success ? Promise.resolve(response) : Promise.reject(response);
            }
            else {
                console.warn('Request hit to unmocked url', url);
                return Promise.reject(new Error(url + " was hit but not mocked " + lastBodyOnlyMismatch));
            }
        },
        post: function (url, body, headers) {
            var lastBodyOnlyMismatch = '';
            var foundHandler = handlers.filter(function (handler) {
                if (handler.request.method === 'POST') {
                    var handlerBody = handler.request.body;
                    var bodyMatch = !!body && compareBody(body, handlerBody);
                    var isWildcard = handler.request.url === '*';
                    var urlsMatch = (handler.request.url === url);
                    if (urlsMatch && !bodyMatch) {
                        // tslint:disable-next-line:max-line-length
                        lastBodyOnlyMismatch = "Found match by url but body does not match. Expected: " + JSON.stringify(handlerBody) + " but got " + JSON.stringify(body);
                    }
                    return isWildcard || urlsMatch && (bodyMatch || handlerBody === '*');
                }
                else {
                    return false;
                }
            })[0];
            requests = __spreadArrays([{ method: 'POST', url: url, body: body, headers: headers }], requests);
            if (foundHandler) {
                foundHandler.onMatch();
                var success = foundHandler.success, response = foundHandler.response;
                return success ? Promise.resolve(response) : Promise.reject(response);
            }
            else {
                console.warn('Request hit to unmocked url', url, lastBodyOnlyMismatch);
                return Promise.reject(new Error(url + " was hit but not mocked. " + lastBodyOnlyMismatch));
            }
        },
        delete: function (url, headers) {
            var foundHandler = handlers.filter(function (handler) {
                var isWildcard = handler.request.url === '*';
                return isWildcard || handler.request.url === url && handler.request.method === 'DELETE';
            })[0];
            requests = __spreadArrays([{ method: 'DELETE', url: url, headers: headers }], requests);
            if (foundHandler) {
                var success = foundHandler.success, response = foundHandler.response;
                foundHandler.onMatch();
                return success ? Promise.resolve(response) : Promise.reject(response);
            }
            else {
                console.warn('Request hit to unmocked url', url);
                return Promise.reject(new Error("Not mocked: [" + url + "]"));
            }
        },
    };
    return __assign(__assign({}, testKit), http);
};
//# sourceMappingURL=test-kit.js.map